import React, { useEffect, useMemo } from 'react';
import { ConfigProvider } from 'antd';
import type { ConfigProviderProps } from 'antd';

import {
    getUIBundle,
    getUILocale,
    setUILocale,
    type SupportedLocale,
    type UITranslations,
    UITextProvider,
} from '../i18n';

export interface UIProviderProps {
    /** Selects the shipped bundle. Defaults to the current module-level locale (English until `setUILocale` is called). */
    locale?: SupportedLocale;
    /** Partial override merged over the shipped bundle for this locale — one-tenant wording tweaks without forking the catalog. */
    overrides?: Partial<{ [K in keyof UITranslations]: Partial<UITranslations[K]> }>;
    /** Passed straight through to antd's `ConfigProvider locale`, so apps wire antd's locale once here instead of a second wrapper. */
    antdLocale?: ConfigProviderProps['locale'];
    children: React.ReactNode;
}

/**
 * Mounted once at the app shell:
 * `<UIProvider locale={i18n.language === 'vi' ? 'vi' : 'en'} antdLocale={viVN}>`.
 * Language switching then propagates to every `packages/ui` atomic/composite
 * with zero per-call-site work.
 */
export const UIProvider: React.FC<UIProviderProps> = ({
    locale = getUILocale(),
    overrides,
    antdLocale,
    children,
}) => {
    // Keep the module-level default in sync, so non-React call sites (or a
    // component that reads `getUIBundle()` outside this context) see the
    // same locale the provider is actually rendering.
    useEffect(() => {
        setUILocale(locale);
    }, [locale]);

    const value = useMemo<UITranslations>(() => {
        const base = getUIBundle(locale);
        if (!overrides) return base;
        // Per-bundle shallow merge, so a tenant can override a single string
        // without having to restate the whole bundle. Every bundle in
        // `UITranslations` must be listed here — a missing one would silently
        // drop the caller's overrides for it (the return type catches omissions).
        return {
            common: { ...base.common, ...overrides.common },
            upload: { ...base.upload, ...overrides.upload },
            password: { ...base.password, ...overrides.password },
            // EAA-1839: added when `pageHeader` joined `UITranslations`; every
            // bundle key must be merged here or a tenant override for it would
            // silently be dropped (see the comment above).
            pageHeader: { ...base.pageHeader, ...overrides.pageHeader },
            // EAA-2026: GroupedList / CollapsibleSection header affordance.
            collapsible: { ...base.collapsible, ...overrides.collapsible },
        };
    }, [locale, overrides]);

    return (
        <ConfigProvider locale={antdLocale}>
            <UITextProvider value={value}>{children}</UITextProvider>
        </ConfigProvider>
    );
};
